// src/components/admin/ChangeRoleModal.tsx
// ============================================================
// Summit League v2 — Change User Role Modal
//
// Displays role radio options + required reason.
// Used from UserDetailPage.
// ============================================================

import React, { useState } from 'react';

interface ChangeRoleModalProps {
  isOpen: boolean;
  username: string;
  currentRole: string;
  onClose: () => void;
  onSubmit: (data: { role: 'user' | 'moderator' | 'admin'; reason: string }) => void;
  isSubmitting: boolean;
}

const ROLES: { value: 'user' | 'moderator' | 'admin'; label: string; desc: string }[] = [
  { value: 'user', label: 'User', desc: 'Standard user — can create teams and play matches' },
  { value: 'moderator', label: 'Moderator', desc: 'Can view admin dashboard and manage disputes' },
  { value: 'admin', label: 'Admin', desc: 'Full admin access — can manage users, seasons, and settings' },
];

export default function ChangeRoleModal({
  isOpen,
  username,
  currentRole,
  onClose,
  onSubmit,
  isSubmitting,
}: ChangeRoleModalProps) {
  const [selectedRole, setSelectedRole] = useState(currentRole);
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const isValid = selectedRole !== currentRole && reason.trim().length >= 3;

  const handleSubmit = () => {
    onSubmit({
      role: selectedRole as 'user' | 'moderator' | 'admin',
      reason: reason.trim(),
    });
  };

  return (
    <div className="sl-modal-backdrop" onClick={onClose}>
      <div
        className="sl-modal sl-role-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Change role for ${username}`}
      >
        <h3 className="sl-modal-title">Change Role — {username}</h3>

        <div className="sl-role-options">
          {ROLES.map((r) => (
            <label
              key={r.value}
              className={`sl-role-option ${
                selectedRole === r.value ? 'sl-role-option--selected' : ''
              } ${r.value === currentRole ? 'sl-role-option--current' : ''}`}
            >
              <input
                type="radio"
                name="role"
                value={r.value}
                checked={selectedRole === r.value}
                onChange={() => setSelectedRole(r.value)}
                className="sl-role-radio"
              />
              <div className="sl-role-option-content">
                <span className="sl-role-option-label">
                  {r.label}
                  {r.value === currentRole && (
                    <span className="sl-role-current-tag">current</span>
                  )}
                </span>
                <span className="sl-role-option-desc">{r.desc}</span>
              </div>
            </label>
          ))}
        </div>

        <div className="sl-ban-section">
          <label className="sl-ban-label">
            Reason <span className="sl-required">*</span>
          </label>
          <textarea
            className="sl-ban-textarea"
            placeholder="Provide a reason for this role change..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={500}
          />
        </div>

        <div className="sl-modal-actions">
          <button
            type="button"
            className="sl-modal-button sl-modal-button-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sl-modal-button sl-modal-button-primary"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update Role'}
          </button>
        </div>
      </div>
    </div>
  );
}
